/**
 * 구간별 운동 지표 아티팩트(`episode_timeline`) 본문을 파싱하고 로드한다(3번째 RTT).
 *
 * 본문 크기는 구간 x 채널 x 지표의 곱이라 수십~수백 KB 다(창원 ep26 실측 145 KB).
 * 그래서 응답에 포함하지 않고 signed URL 로 직접 로드한다. 운동 보기를 켤 때만 받는다.
 *
 * Owns:
 * - 아티팩트 JSON 을 구간·에피소드 전체 요약 모델로 파싱하고 검증한다.
 *   `schema_version` 4 만 받는다.
 * - 개별 타임라인 준비 상태와 조회 오류를 구분한다. 형식 불명·404는 unavailable이다.
 *
 * Does not own:
 * - 상세 GET 과 첫 응답 모델(`../diagnosticsSeries`).
 * - 시간별 시계열 산출물(`./motionSeries`).
 * - 지표 이름·단위 문구(`./motionTerms`).
 *
 * 백엔드 계약(hatchery-data `schemas/episode_timeline.py`, registry `worker/derived/timeline.py`):
 * - `events` 는 `t_start` 오름차순이다. **길이 0 인 경계 구간이 섞여 온다**(창원 ep26 은 26개 중 7개).
 * - 지표를 못 낸 구간에는 `unavailable`(예: `no_rows`)이 있고 `metrics` 가 비어 있다.
 * - 그리퍼 같은 raw 채널은 `value.N` 을 낸다.
 * - pose 채널은 `speed`·`speed_change_rate`·`acceleration` 을 낸다.
 * - 상태 구역은 구간·에피소드마다 `findings.states` 에 있다.
 * - `episode` 는 원본 전체 범위로 따로 잰 요약이다. 구간 요약을 합쳐 대신할 수 없다.
 */

import type { DiagnosticsSeriesResp } from "@/api";
import { fetchSignedJson, isHttpClientError } from "@/http";
import type { EpisodeDiagnosticDetail } from "../diagnosticsSeries";

/** 소비하는 본문 스키마 버전. 다르면 형식 불명으로 degrade 한다(위상 그리드와 같은 규약). */
export const TIMELINE_ARTIFACT_SCHEMA_VERSION = 4;

/** 지표 극값 하나. 값과 모델의 평가 시각을 가진다. */
export type TimelineExtremum = {
  value: number;
  timestamp: number;
};

/**
 * 한 구간·한 채널의 지표 하나.
 *
 * `mean`·`rms` 는 시간 가중이고 `p95` 는 표본 분위수다. 유효 표본이 없으면 통계가 전부 null
 * 로 온다. 그래서 값을 0 으로 바꾸지 않는다. BE 가 단위를 내지 않아 `unit` 은 **항상 null**
 * 이다. 화면 단위는 `./motionTerms` 가 채널 계약을 보고 붙인다.
 */
export type TimelineMetric = {
  unit: string | null;
  derivativeOrder: number;
  count: number;
  validDurationSeconds: number;
  mean: number | null;
  p95: number | null;
  rms: number | null;
  min: TimelineExtremum | null;
  max: TimelineExtremum | null;
};

/** 한 구간 안 한 채널의 행 범위와 지표. `unavailable` 은 지표를 생략한 사유다. */
export type TimelineChannel = {
  nRows: number;
  validRows: number;
  metrics: ReadonlyMap<string, TimelineMetric>;
  unavailable: string | null;
};

/** 구간 안에서 상태가 유지된 구역. `reason` 은 `unknown` 의 원인이다. */
export type TimelineStateInterval = {
  tStart: number;
  tEnd: number;
  state: string;
  reason: string | null;
};

/** 자동 상태 구간 하나(또는 에피소드 전체). 채널마다 상태 구역과 지표를 가진다. */
export type TimelineEvent = {
  tStart: number;
  tEnd: number;
  states: ReadonlyMap<string, readonly TimelineStateInterval[]>;
  channels: ReadonlyMap<string, TimelineChannel>;
};

export type EpisodeTimeline = {
  // 산출 방식. 캡션이 "무엇으로 잰 값인가"를 알릴 때 쓴다.
  method: string;
  events: readonly TimelineEvent[];
  // 에피소드 전체 요약. 유효 timestamp 가 하나도 없으면 null 이다.
  episode: TimelineEvent | null;
};

/**
 * 로드 결과. 그리드와 같은 세 가지 경우를 가진다.
 * `unavailable` 은 아직 산출 전(객체 없음·형식 불명)이다.
 */
export type EpisodeTimelineResult =
  | { kind: "timeline"; timeline: EpisodeTimeline }
  | { kind: "status"; series: DiagnosticsSeriesResp }
  | { kind: "unavailable" };

/**
 * 상세 응답이 가리키는 타임라인 아티팩트를 로드한다.
 *
 * 경우는 다음과 같다.
 * - 상세나 series 가 없으면 unavailable.
 * - 개별 타임라인이 ready 가 아니면 status. 문구는 소비부가 고른다.
 * - URL 없음·404·형식 불명이면 unavailable.
 * 그 밖의 네트워크 오류는 전파한다. 소비부가 에러 자리에 렌더한다.
 *
 * Args:
 *   detail: 첫 RTT 결과. null 이면 진단 자체가 없다.
 *   options.signal: 다른 에피소드로 이동할 때 요청을 끊는 신호.
 */
export async function loadEpisodeTimelineFor(
  detail: EpisodeDiagnosticDetail | null,
  options: { signal?: AbortSignal } = {},
): Promise<EpisodeTimelineResult> {
  if (detail === null) {
    return { kind: "unavailable" };
  }
  const series = detail.timelineSeries;
  if (series === null) return { kind: "unavailable" };
  if (series.status !== "ready") {
    return { kind: "status", series };
  }
  if (series.url === null) {
    return { kind: "unavailable" };
  }
  let raw: unknown;
  try {
    raw = await fetchSignedJson<unknown>(series.url, options.signal);
  } catch (error) {
    // 완료 기록과 저장소가 어긋난 경우는 파일 부재로 표시한다.
    if (isHttpClientError(error) && error.status === 404) {
      return { kind: "unavailable" };
    }
    throw error;
  }
  const timeline = parseEpisodeTimeline(raw);
  return timeline === null ? { kind: "unavailable" } : { kind: "timeline", timeline };
}

/**
 * 아티팩트 본문을 파싱한다. `schema_version` 불일치나 형식 불명이면 null 을 반환한다.
 * 위상 그리드 파서와 같은 규약이다.
 */
export function parseEpisodeTimeline(raw: unknown): EpisodeTimeline | null {
  if (raw === null || typeof raw !== "object") {
    return null;
  }
  const payload = raw as {
    schema_version?: unknown;
    method?: unknown;
    events?: unknown;
    episode?: unknown;
  };
  if (payload.schema_version !== TIMELINE_ARTIFACT_SCHEMA_VERSION) {
    return null;
  }
  if (!Array.isArray(payload.events)) {
    return null;
  }
  const events: TimelineEvent[] = [];
  for (const value of payload.events) {
    const event = parseEvent(value);
    if (event !== null) {
      events.push(event);
    }
  }
  return {
    method: typeof payload.method === "string" ? payload.method : "",
    events,
    episode: parseEvent(payload.episode),
  };
}

// ── 파싱 내부 ──

function parseEvent(value: unknown): TimelineEvent | null {
  if (value === null || typeof value !== "object") {
    return null;
  }
  const event = value as {
    t_start?: unknown;
    t_end?: unknown;
    findings?: { states?: unknown } | null;
    channels?: unknown;
  };
  if (typeof event.t_start !== "number" || typeof event.t_end !== "number") {
    return null;
  }
  return {
    tStart: event.t_start,
    tEnd: event.t_end,
    states: parseStates(event.findings?.states),
    channels: parseChannels(event.channels),
  };
}

function parseStates(value: unknown): ReadonlyMap<string, readonly TimelineStateInterval[]> {
  const states = new Map<string, readonly TimelineStateInterval[]>();
  if (value === null || typeof value !== "object") {
    return states;
  }
  for (const [channel, raw] of Object.entries(value as Record<string, unknown>)) {
    if (!Array.isArray(raw)) {
      continue;
    }
    const intervals: TimelineStateInterval[] = [];
    for (const entry of raw) {
      if (entry === null || typeof entry !== "object") {
        continue;
      }
      const interval = entry as {
        t_start?: unknown;
        t_end?: unknown;
        state?: unknown;
        reason?: unknown;
      };
      if (
        typeof interval.t_start !== "number" ||
        typeof interval.t_end !== "number" ||
        typeof interval.state !== "string"
      ) {
        continue;
      }
      intervals.push({
        tStart: interval.t_start,
        tEnd: interval.t_end,
        state: interval.state,
        reason: typeof interval.reason === "string" ? interval.reason : null,
      });
    }
    states.set(channel, intervals);
  }
  return states;
}

function parseChannels(value: unknown): ReadonlyMap<string, TimelineChannel> {
  const channels = new Map<string, TimelineChannel>();
  if (value === null || typeof value !== "object") {
    return channels;
  }
  for (const [name, raw] of Object.entries(value as Record<string, unknown>)) {
    if (raw === null || typeof raw !== "object") {
      continue;
    }
    const channel = raw as {
      n_rows?: unknown;
      valid_rows?: unknown;
      metrics?: unknown;
      unavailable?: unknown;
    };
    channels.set(name, {
      nRows: typeof channel.n_rows === "number" ? channel.n_rows : 0,
      validRows: typeof channel.valid_rows === "number" ? channel.valid_rows : 0,
      metrics: parseMetrics(channel.metrics),
      unavailable: typeof channel.unavailable === "string" ? channel.unavailable : null,
    });
  }
  return channels;
}

function parseMetrics(value: unknown): ReadonlyMap<string, TimelineMetric> {
  const metrics = new Map<string, TimelineMetric>();
  if (value === null || typeof value !== "object") {
    return metrics;
  }
  for (const [name, raw] of Object.entries(value as Record<string, unknown>)) {
    if (raw === null || typeof raw !== "object") {
      continue;
    }
    const metric = raw as {
      unit?: unknown;
      derivative_order?: unknown;
      count?: unknown;
      valid_duration_seconds?: unknown;
      mean?: unknown;
      p95?: unknown;
      rms?: unknown;
      min?: unknown;
      max?: unknown;
    };
    metrics.set(name, {
      unit: typeof metric.unit === "string" ? metric.unit : null,
      derivativeOrder: typeof metric.derivative_order === "number" ? metric.derivative_order : 0,
      count: typeof metric.count === "number" ? metric.count : 0,
      validDurationSeconds:
        typeof metric.valid_duration_seconds === "number" ? metric.valid_duration_seconds : 0,
      mean: finiteOrNull(metric.mean),
      p95: finiteOrNull(metric.p95),
      rms: finiteOrNull(metric.rms),
      min: parseExtremum(metric.min),
      max: parseExtremum(metric.max),
    });
  }
  return metrics;
}

function parseExtremum(value: unknown): TimelineExtremum | null {
  if (value === null || typeof value !== "object") {
    return null;
  }
  const extremum = value as { value?: unknown; timestamp?: unknown };
  if (typeof extremum.value !== "number" || typeof extremum.timestamp !== "number") {
    return null;
  }
  return { value: extremum.value, timestamp: extremum.timestamp };
}

/** 통계는 표본이 없으면 null 로 온다. 0 으로 바꾸면 "멈춰 있었다"로 읽힌다. */
function finiteOrNull(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}
